import { createBrowserRouter } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import AuthActionPage from './pages/AuthActionPage';
import CalendarScreen from './pages/CalendarScreen';
import CRMDashboardPage from './pages/CRMDashboardPage';
import JobsDatabasePage from './pages/JobsDatabasePage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import InstallerPortal from './pages/InstallerPortal';
import InstallerJobDetail from './pages/InstallerJobDetail';
import ShopPortalScreen from './pages/shop/ShopPortalScreen';
import JobDetailPage from './pages/shop/JobDetailPage';
import CutlistDashboard from './pages/cutlist/CutlistDashboard';
import NewCutlistJob from './pages/cutlist/NewCutlistJob';
import CutListPage from './pages/cutlist/CutListPage';
import ShopOrderPage from './pages/cutlist/ShopOrderPage';
import EstimatePage from './pages/cutlist/EstimatePage';
import ProtectedRoute from './components/ProtectedRoute';
import QuickQuotePage from './pages/estimates/QuickQuotePage';
import EstimatesListPage from './pages/estimates/EstimatesListPage';
import CustomerHubPage from './pages/estimates/CustomerHubPage';
import CreateJobOrderPage from './pages/estimates/CreateJobOrderPage';
import JobOrdersReviewPage from './pages/estimates/JobOrdersReviewPage';
import { ShopProvider } from './contexts/ShopContext';
import EstimatePublicView from './pages/public/EstimatePublicView';
import ContractPublicView from './pages/public/ContractPublicView';
import ContractBuilderPage from './pages/estimates/ContractBuilderPage';
import { JobProvider as CutlistJobProvider } from './contexts/CutlistJobContext';
import CommunicationsPage from './pages/CommunicationsPage';
import OfficeIntakePage from './pages/OfficeIntakePage';
import ArchivePage from './pages/ArchivePage';
import WebsitePage from './pages/WebsitePage';

// ── Super-admin guard — only nk@thewindowplacellc.com ────────────────────────
function NoahOnly({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  if (currentUser?.email !== 'nk@thewindowplacellc.com') {
    return <Navigate to="/" replace />;
  }
  return children;
}

const MANAGER_ROLES = ['manager', 'installManager', 'sales', 'officeManager'];
// Cutlist Dashboard opened up to every non-installer/shop role, same as the
// rest of the sidebar — only Job Orders and Settings stay restricted.
const CUTLIST_ROLES = MANAGER_ROLES;

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />
  },
  // ── Public auth action handler (password reset, etc.) ─────────────────────
  // Lives on our own domain so password managers associate saved passwords
  // with twp-calendar-61a9c.web.app / production.thewindowplacellc.com.
  {
    path: '/auth/action',
    element: <AuthActionPage />
  },
  // ── Public estimate routes ─────────────────────────────────────────────────
  // /estimate/:token — legacy route (old links still work)
  {
    path: '/estimate/:token',
    element: <EstimatePublicView />
  },
  // /e/:token — new secure route used by windowplace-documents.web.app
  {
    path: '/e/:token',
    element: <EstimatePublicView />
  },
  {
    path: '/contract/:token',
    element: <ContractPublicView />
  },
  {
    path: '/',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <CalendarScreen />
      </ProtectedRoute>
    )
  },
  {
    path: '/crm',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <CRMDashboardPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/jobs',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <JobsDatabasePage />
      </ProtectedRoute>
    )
  },
  {
    path: '/reports',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <ReportsPage />
      </ProtectedRoute>
    )
  },
  // ── Archive — Drive archive + Hub record search ────────────────────────────
  {
    path: '/archive',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <ArchivePage />
      </ProtectedRoute>
    )
  },
  // ── Website (thewindowplacellc.com): leads, requests, design studio ────────
  // The website's server checks the Hub role again on every request.
  ...[['/website', 'overview'], ['/website/leads', 'overview'], ['/website/requests', 'overview'], ['/website/design', 'design']].map(([path, view]) => ({
    path,
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <WebsitePage key="website" view={view} />
      </ProtectedRoute>
    )
  })),
  // Old name for the same page; keeps any saved links working.
  {
    path: '/assistant',
    element: <Navigate to="/archive" replace />
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <NoahOnly>
          <SettingsPage />
        </NoahOnly>
      </ProtectedRoute>
    )
  },
  // ── Office Intake ──────────────────────────────────────────────────────────
  {
    path: '/office-intake',
    element: (
      <ProtectedRoute allowedRoles={['manager', 'installManager', 'sales', 'officeManager', 'installer', 'shop_worker']}>
        <OfficeIntakePage />
      </ProtectedRoute>
    )
  },
  // ── Communications — all roles ─────────────────────────────────────────────
  {
    path: '/communications',
    element: (
      <ProtectedRoute allowedRoles={['manager', 'installManager', 'sales', 'officeManager', 'installer', 'shop_worker']}>
        <CommunicationsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/installer',
    element: (
      <ProtectedRoute allowedRoles={['installer']}>
        <InstallerPortal />
      </ProtectedRoute>
    )
  },
  {
    path: '/installer/job/:jobId',
    element: (
      <ProtectedRoute allowedRoles={['installer']}>
        <InstallerJobDetail />
      </ProtectedRoute>
    )
  },
  {
    path: '/shop',
    element: (
      <ProtectedRoute allowedRoles={['shop_worker']}>
        <ShopProvider>
          <ShopPortalScreen />
        </ShopProvider>
      </ProtectedRoute>
    )
  },
  {
    path: '/shop/job/:jobId',
    element: (
      <ProtectedRoute allowedRoles={['shop_worker']}>
        <ShopProvider>
          <JobDetailPage />
        </ShopProvider>
      </ProtectedRoute>
    )
  },
  {
    path: '/cutlist',
    element: (
      <ProtectedRoute allowedRoles={CUTLIST_ROLES}>
        <CutlistJobProvider>
          <CutlistDashboard />
        </CutlistJobProvider>
      </ProtectedRoute>
    )
  },
  {
    path: '/cutlist/new',
    element: (
      <ProtectedRoute allowedRoles={CUTLIST_ROLES}>
        <NewCutlistJob />
      </ProtectedRoute>
    )
  },
  {
    path: '/cutlist/:id/cutlist',
    element: (
      <ProtectedRoute allowedRoles={CUTLIST_ROLES}>
        <CutlistJobProvider>
          <CutListPage />
        </CutlistJobProvider>
      </ProtectedRoute>
    )
  },
  {
    path: '/cutlist/:id/shoporder',
    element: (
      <ProtectedRoute allowedRoles={CUTLIST_ROLES}>
        <CutlistJobProvider>
          <ShopOrderPage />
        </CutlistJobProvider>
      </ProtectedRoute>
    )
  },
  {
    path: '/estimates',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <EstimatesListPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/job-orders',
    element: (
      <ProtectedRoute allowedRoles={['officeManager']}>
        <JobOrdersReviewPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/estimates/job-order/:estimateId',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <CreateJobOrderPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/estimates/customer/:customerName',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <CustomerHubPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/estimates/new',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <QuickQuotePage />
      </ProtectedRoute>
    )
  },
  {
    path: '/estimates/contract/:estimateId',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <ContractBuilderPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/estimates/edit/:id',
    element: (
      <ProtectedRoute allowedRoles={MANAGER_ROLES}>
        <QuickQuotePage />
      </ProtectedRoute>
    )
  },
  {
    path: '/cutlist/:id/estimate',
    element: (
      <ProtectedRoute allowedRoles={CUTLIST_ROLES}>
        <CutlistJobProvider>
          <EstimatePage />
        </CutlistJobProvider>
      </ProtectedRoute>
    )
  }
]);